import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Text, useTheme } from '@Sage/hoi-poi-ui';
import { getVar } from '@Sage/ssm-utils';

import { DoughnutChart } from 'components/charts';
import { getLiteral } from 'utils/getLiteral';

const DeliveryHealth = memo(({ health }) => {
    const theme = useTheme();
    const deliveredHex = getVar(theme.colors.semantic.positive500);
    const bouncedHex = getVar(theme.colors.semantic.negative450);
    const ratePoorHex = getVar(theme.colors.semantic.negative500);
    const rateMediumHex = getVar(theme.colors.semantic.focus500);

    const { chartData, deliveredRate, total } = useMemo(() => {
        if (!health) {
            return { chartData: [0, 0], deliveredRate: 0, total: 0 };
        }

        const delivered = health.delivered || 0;
        const bounced = health.bounced || 0;
        const total = delivered + bounced;

        const deliveredRate = total > 0 ? Math.round((delivered / total) * 100) : 0;

        return {
            chartData: [delivered, bounced],
            deliveredRate,
            total,
        };
    }, [health]);

    const colors = useMemo(() => [deliveredHex, bouncedHex], [deliveredHex, bouncedHex]);

    const centerValueColor = useMemo(() => {
        if (deliveredRate <= 35) return ratePoorHex;
        if (deliveredRate <= 65) return rateMediumHex;
        return deliveredHex;
    }, [deliveredHex, deliveredRate, rateMediumHex, ratePoorHex]);

    if (!health || total === 0) return null;

    return (
        <div className="fm-delivery-health">
            <Text type="body">
                {getLiteral('label_marketing_campaigns_metrics_delivery_health')}
            </Text>
            <div className="fm-delivery-health__chart">
                <DoughnutChart
                    labels={[
                        getLiteral('label_marketing_campaigns_metrics_delivered_plural'),
                        getLiteral('label_marketing_campaigns_metrics_bounces'),
                    ]}
                    data={chartData}
                    colors={colors}
                    centerValue={`${deliveredRate}%`}
                    centerValueColor={centerValueColor}
                    centerLabel={getLiteral('label_marketing_campaigns_metrics_delivered_plural')}
                />
            </div>
            <div className="fm-delivery-health__legend">
                <div className="fm-delivery-health__legend-item">
                    <span
                        className="fm-delivery-health__legend-dot"
                        style={{ backgroundColor: deliveredHex }}
                    />
                    <Text type="caption" color="neutral700">
                        {getLiteral('label_marketing_campaigns_metrics_delivered_plural')}
                    </Text>
                    <Text type="caption" bold>
                        {health.delivered?.toLocaleString()}
                    </Text>
                </div>
                <div className="fm-delivery-health__legend-item">
                    <span
                        className="fm-delivery-health__legend-dot"
                        style={{ backgroundColor: bouncedHex }}
                    />
                    <Text type="caption" color="neutral700">
                        {getLiteral('label_marketing_campaigns_metrics_bounces')}
                    </Text>
                    <Text type="caption" bold color={bouncedHex}>
                        {health.bounced?.toLocaleString()}
                    </Text>
                </div>
            </div>
        </div>
    );
});

DeliveryHealth.displayName = 'DeliveryHealth';

DeliveryHealth.propTypes = {
    health: PropTypes.shape({
        delivered: PropTypes.number,
        bounced: PropTypes.number,
    }),
};

export default DeliveryHealth;
